"use client";

/**
 * @file Ads Analytics プロジェクト作成ダイアログコンポーネント。
 * プロジェクト名・カラーを入力して ADS_ANALYTICS プロジェクトを作成する。
 */

import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2Icon } from "lucide-react";
import { useParams } from "next/navigation";
import React, { useId } from "react";
import { toast } from "sonner";
import { AdsBetaBadge } from "@/app/[lang]/[workspaceId]/ads/_components/ads-beta-badge";
import { Button } from "@/components/ui/button";
import { ColorPicker, DEFAULT_COLORS } from "@/components/ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";
import { createProject } from "../../action";

type Props = {
  workspaceId: string;
  trigger: React.ReactNode;
  workspaceUsedColors?: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function pickAdsProjectDefaultColor(usedColors?: string[]) {
  const used = new Set((usedColors ?? []).map((c) => c.toLowerCase()));
  const unused = DEFAULT_COLORS.find((c) => !used.has(c.toLowerCase()));
  if (unused) return unused;
  return DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)]!;
}

export function CreateAdsProjectDialog({
  workspaceId,
  trigger,
  workspaceUsedColors,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const { t } = useLingui();
  const router = useRouter();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const params = useParams<{ lang?: string }>();
  const lang = params?.lang ?? "en";
  const id = useId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [color, setColor] = React.useState(() =>
    pickAdsProjectDefaultColor(workspaceUsedColors),
  );
  const [isPending, startTransition] = React.useTransition();

  const open = controlledOpen !== undefined ? controlledOpen : isOpen;
  const handleOpenChange = (next: boolean) => {
    onOpenChange?.(next);
    setIsOpen(next);
    if (next) {
      setColor(pickAdsProjectDefaultColor(workspaceUsedColors));
    } else {
      setName("");
      setColor(pickAdsProjectDefaultColor(workspaceUsedColors));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    startTransition(async () => {
      const result = await createProject(
        workspaceId,
        name.trim(),
        "ads",
        color,
      );
      if (result.errors.length > 0) {
        toast.error(result.errors[0]);
        return;
      }
      toast.success(
        t`Ads Analytics project created. Connect your data sources in settings.`,
      );
      handleOpenChange(false);
      router.refresh();
      if (result.projectId) {
        router.push(
          `/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(result.projectId)}/settings`,
        );
      }
      refreshWorkspaceSidebar("project-mutation");
    });
  };

  return (
    <>
      {React.cloneElement(
        trigger as React.ReactElement<{
          onClick?: () => void;
        }>,
        {
          onClick: () => handleOpenChange(true),
        },
      )}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-1.5">
                <Trans>Create Ads Analytics project</Trans>
                <AdsBetaBadge />
              </span>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Create a new Ads Analytics project to connect your advertising
                data sources.
              </Trans>{" "}
              <Trans>
                Ads Analytics is currently in beta. Features and behavior may
                change without notice.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${id}-name`}>
                <Trans>Project name</Trans>
              </Label>
              <Input
                id={`${id}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t`My Ads Analytics`}
                required
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>
                <Trans>Project color</Trans>
              </Label>
              <ColorPicker
                value={color}
                onChange={setColor}
                colors={DEFAULT_COLORS}
                usedColors={workspaceUsedColors}
                required
                disabled={isPending}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" disabled={isPending || !name.trim()}>
                {isPending && <Loader2Icon className="h-4 w-4 animate-spin" />}
                <Trans>Create</Trans>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
